import {
    faBasketShopping,
    faCalculator,
    faCalendar,
    faLayerGroup,
    faPerson,
    faSackDollar,
    faSave,
    faScaleBalanced,
    faSeedling,
    faWallet,
    faWeightHanging
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "components/common/button/Button";
import Input from "components/common/form/inputs/Input";
import ItemView, { ItemViewPropsType } from "components/common/views/ItemView";
import { useTranslation } from "react-i18next";
import { SaleViewPageQuery } from "types/graphql";
import { formatMoney } from "utilities/datalist/datalist-sort-utilities";
import { DateFormats, DateUtil } from "utilities/date-utilities";
import { TOTAL_ROW_INDEX } from "../SaleMultiUpdatePage";
import WalletDropdown from "../../../../components/entities/app/person/wallet/WalletDropdown";

type FormItem = {
    kg: string | number,
    pricePerKg: string | number,
    total: string | number,
    wallet?: {
        id: number,
        name: string,
    } | null,
}

type SaleMultiupdatePageCardViewPropsType = {
    model: Exclude<SaleViewPageQuery["sale"], null | undefined>,
    formModel: FormItem
    name: string,
    onChangeValue: (id: number, value: Partial<FormItem>) => void,
    onKgPerCartChange: (value: number) => void,
    calculate: (id: number, force: boolean) => void;
    calculateKg: (id: number) => void;
    onSave: (id: number) => Promise<void>;
    kgPerCart: string,
    setKgPerCart: (value: string) => void;
}

const SaleMultiupdatePageCardView = (props: SaleMultiupdatePageCardViewPropsType) => {
    const [t] = useTranslation();
    const isTotalRow = props.model.id === TOTAL_ROW_INDEX;

    const properties: ItemViewPropsType['model'] = [
        {
            items: [
                {
                    icon: faCalendar,
                    label: 'fields.date.shortName',
                    name: 'date',
                    value: DateUtil.format(props.model?.date, DateFormats.SHORT)
                },
                {
                    icon: faPerson,
                    label: 'fields.person.shortName',
                    name: 'person',
                    value: props.model.person?.name
                },
                {
                    icon: faSeedling,
                    label: 'fields.fruit.shortName',
                    value: props.model.fruit?.name,
                    name: 'fruit'
                }
            ],
            name: 'data-person',
            className: {
                compact: 'grid grid-cols-3 text-center'
            },
            hide: isTotalRow
        },
        {
            items: [
                {
                    name: 'kg',
                    className: "my-auto",
                    value: <Input
                        name="kg"
                        hideErrorMessages
                        type="number"
                        selectTextOnFocus
                        prefix={{ icon: faWeightHanging, name: 'kg' }}
                        value={isTotalRow ? props.kgPerCart : props.formModel?.kg?.toString()}
                        onBlur={v => {
                            if(isTotalRow) props.setKgPerCart('');
                            else if(!v || v === '0') props.onChangeValue(props.model.id, {kg: ''})
                        }}
                        suffix={{ icon: faCalculator, onClick: () => props.calculateKg(props.model.id), name: t('common.calculate'), hidden: () => isTotalRow }}
                        onChange={v => {
                            if(isTotalRow) {
                                props.setKgPerCart(v || '');
                                const nr = Number(v);
                                if(nr) props.onKgPerCartChange(nr);
                            }
                            else props.onChangeValue(props.model.id, {kg: v});
                        }}
                        placeholder="fields.kg.shortName"
                    />
                },
                {
                    name: 'price',
                    value: <Input
                        name="pricePerKg"
                        type="number"
                        selectTextOnFocus
                        hideErrorMessages
                        value={props.formModel?.pricePerKg?.toString()}
                        onBlur={v => {
                            if(isTotalRow) props.onChangeValue(TOTAL_ROW_INDEX, {pricePerKg: ''})
                            else if(!v || v === '0') props.onChangeValue(props.model.id, {pricePerKg: ''})
                        }}
                        prefix={{ icon: faScaleBalanced, name:"total"}}
                        onChange={v => props.onChangeValue(props.model.id, { pricePerKg: v })}
                        placeholder="fields.price-per-kg.shortName"
                    />
                },
                {
                    name: 'total',
                    className: "my-auto",
                    icon: isTotalRow ? faSackDollar : undefined,
                    value: isTotalRow ? formatMoney(props.formModel.total) : <Input
                        name="total"
                        type="number"
                        selectTextOnFocus
                        hideErrorMessages
                        value={props.formModel?.total?.toString()}
                        onBlur={v => {
                            if(!v || v === '0') props.onChangeValue(props.model.id, {total: ''})
                        }}
                        onChange={v => props.onChangeValue(props.model.id, { total: v })}
                        placeholder="fields.total.shortName"
                        onFocus={() => props.calculate(props.model.id, false)}
                        prefix={{ icon: faSackDollar, name:"total"}}
                        suffix={{ icon: faCalculator, onClick: () => props.calculate(props.model.id, true), name: t('common.calculate') }}
                    />
                }
            ],
            name: 'quantity',
        }, {
            name: 'wallet',
            hide: isTotalRow,
            items: [
                {
                    name: 'wallet',
                    value: <WalletDropdown
                        name={"wallet-" + props.model.id}
                        value={props.formModel.wallet || undefined}
                        personId={props.model.person.id}
                        placeholder={t('fields.wallet.shortName')}
                        label={false}
                        prefix={{ icon: faWallet, name: 'wallet' }}
                        onChange={v => props.onChangeValue(props.model.id, { wallet: v })}
                    />
                }
            ],
        }, {
            name: 'save',
            items: [
                {
                    icon: isTotalRow ? faWeightHanging : faLayerGroup,
                    label: 'fields.category.shortName',
                    name: 'category',
                    className: "my-auto",
                    value: isTotalRow ? formatMoney(props.formModel?.kg) : props.model?.category?.name ? t('agriculture.category.' + props.model?.category?.name.toLowerCase()) : '-',
                },
                {
                    icon: faBasketShopping,
                    name: 'cart',
                    value: props.model?.cartQuantity,
                    className: "my-auto"
                },
                {
                    name: 'save',
                    value: <Button title={t(isTotalRow ? 'button.save-all' : 'button.save')} onClick={() => props.onSave(props.model.id)} theme="link primary">
                        <FontAwesomeIcon icon={faSave} />
                    </Button>
                }
            ]
        }
    ];

    return <ItemView name={props.name} model={properties} view={'compact'}  />
};

export default SaleMultiupdatePageCardView;
